/**
 * Owner audit trail — one row per action, never a column that gets overwritten.
 *
 * The owner Activity tab used to be derived entirely from state: `sent_at`,
 * `paid_at`, `proof_uploaded_at`, `metadata.proof_note`. Each of those is a
 * SINGLE value, so sending the same invoice a second time did not add a second
 * entry — it overwrote the first one, and the earlier send vanished from the
 * history. A timeline that loses events is worse than no timeline, because it
 * looks complete.
 *
 * So every owner-facing action writes its own `audit_logs` row here, and the
 * feed renders one entry per row. Two rows for two sends. Nothing is ever
 * rewritten, and an admin edit that touches no invoice at all (a changed IBAN,
 * a new representative) finally shows up too.
 *
 * Rows are tagged `metadata.source = 'owner_profile'` and `metadata.lessor_id`,
 * mirroring how the tenant profile tags `booking_id` — that pair is both the
 * query filter and what the RLS policy in 20260816130100 keys on, so a regular
 * admin can read back the trail they just wrote.
 */
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/lib/supabase-types";

/** Tag every row this module writes carries. Also the RLS predicate. */
export const OWNER_AUDIT_SOURCE = "owner_profile";

export type OwnerAuditEntry = {
  /** The owner the action belongs to — what the Activity tab filters on. */
  lessorId: string | null | undefined;
  /** Stable machine name, e.g. `management_invoice_sent`. Titled in owner-audit-titles. */
  action: string;
  resourceType: string;
  resourceId: string;
  oldValues?: Record<string, Json | undefined> | null;
  newValues?: Record<string, Json | undefined> | null;
  metadata?: Record<string, Json | undefined>;
};

/**
 * Write one audit row. Best-effort by design: a failed audit write must never
 * roll back or surface over the action the admin actually took — that would
 * turn "we could not log this" into "your invoice did not send", which is both
 * false and worse. Failures go to the console; the action stands.
 *
 * An action on an owner we cannot identify is dropped rather than written
 * unaddressed: a row with no lessor_id can never be read back by the feed, so
 * it is storage nobody will ever see.
 */
export async function logOwnerAudit(entry: OwnerAuditEntry): Promise<void> {
  if (!entry.lessorId) return;
  try {
    const { data } = await supabase.auth.getUser();
    await supabase.from("audit_logs").insert({
      user_id: data?.user?.id ?? null,
      action: entry.action,
      resource_type: entry.resourceType,
      resource_id: entry.resourceId,
      old_values: entry.oldValues ?? null,
      new_values: entry.newValues ?? null,
      metadata: {
        ...(entry.metadata ?? {}),
        lessor_id: entry.lessorId,
        source: OWNER_AUDIT_SOURCE,
      },
    });
  } catch (e) {
    console.error("Failed to write owner audit log", e);
  }
}

/** Columns whose value is masked before it reaches the log. */
const MASKED_FIELDS = new Set(["bank_iban"]);

function maskValue(field: string, value: unknown): unknown {
  if (!MASKED_FIELDS.has(field) || typeof value !== "string" || !value)
    return value;
  // "…changed the IBAN, from one ending 4434 to one ending 9021" is the whole
  // audit question. The full number is on the owner record for anyone entitled
  // to it, so the log keeps the answer without keeping a second copy.
  const compact = value.replace(/\s+/g, "");
  return compact.length <= 4 ? compact : `…${compact.slice(-4)}`;
}

/**
 * The fields that actually changed, as two aligned objects.
 *
 * Logging the whole payload on every save would make the timeline claim an
 * admin "updated" fourteen fields they never touched. Only what moved is
 * recorded — and an edit that moved nothing writes no row at all (null).
 */
export function diffOwnerFields<T extends Record<string, unknown>>(
  before: T | null | undefined,
  after: T | null | undefined
): { old: Record<string, unknown>; next: Record<string, unknown> } | null {
  if (!before || !after) return null;
  const old: Record<string, unknown> = {};
  const next: Record<string, unknown> = {};
  for (const key of Object.keys(after)) {
    const a = before[key] ?? null;
    const b = after[key] ?? null;
    // Empty string and null both mean "not set" in these forms; treating them
    // as different would log a change every time an empty field is re-saved.
    const norm = (v: unknown) => (v === "" ? null : v);
    if (Object.is(norm(a), norm(b))) continue;
    old[key] = maskValue(key, a);
    next[key] = maskValue(key, b);
  }
  return Object.keys(next).length ? { old, next } : null;
}
